import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';

interface NavBadgeProps {
  count: number;
  /** The badge's class in its sidebar's stylesheet. */
  className: string;
  /** Added with an icon: the sidebar's stylesheet makes it a plain dot in the icon rail. */
  iconicClassName?: string;
  /** Says what is counted, beside the number. */
  icon?: string;
  /** An i18n key with _one/_other naming what is counted - the bubble on hover, and what a screen reader says. */
  tooltipKey?: string;
  /** Only a dot, without the number: on a closed group, whose item inside carries the count. */
  asDot?: boolean;
  /** The dot's class in the sidebar's stylesheet. */
  dotClassName?: string;
}

/**
 * A count beside a sidebar item, shared by the coordinator's sidebar and the management area's.
 *
 * HAT-1428 (Chaya, 20.10): a bare «1» beside «נתמכים» does not say what it counts, so a badge can
 * carry an icon of what it counts and a bubble that says it in words. On a closed group and in the
 * icon rail it is only a dot (Chaya, 20.10): there it says that something waits inside, and the item
 * says what and how many.
 */
const NavBadge = ({ count, className, iconicClassName, icon, tooltipKey, asDot = false, dotClassName }: NavBadgeProps) => {
  const { t } = useTranslation();
  const id = useId();
  const text = tooltipKey ? t(tooltipKey, { count }) : undefined;

  return (
    <>
      {asDot ? (
        <span id={id} className={`${className} ${dotClassName ?? ''}`.trim()} role="img" aria-label={text ?? String(count)} />
      ) : (
        <span
          id={id}
          className={`${className} ${icon && iconicClassName ? iconicClassName : ''}`.trim()}
          role={text ? 'img' : undefined}
          aria-label={text}
        >
          {icon && <TintedIcon icon={icon} size={16} color="currentColor" />}
          {count > 99 ? '99+' : count}
        </span>
      )}
      {text && (
        <CustomTooltip targetId={id}>
          <p className="body-xs-sm-regular">{text}</p>
        </CustomTooltip>
      )}
    </>
  );
};

export default NavBadge;
